// Minimal AWS event-stream codec + PCM helpers for Amazon Transcribe streaming over a
// presigned WebSocket. Message framing (all big-endian):
//   [4B total length][4B headers length][4B prelude CRC32][headers][payload][4B message CRC32]
// Each header: [1B name length][name][1B value type (7 = string)][2B value length][value].

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c;
  }
  return table;
})();

export function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

function encodeHeaders(headers) {
  const parts = [];
  for (const [name, value] of Object.entries(headers)) {
    const nameBytes = textEncoder.encode(name);
    const valueBytes = textEncoder.encode(value);
    const part = new Uint8Array(1 + nameBytes.length + 1 + 2 + valueBytes.length);
    const view = new DataView(part.buffer);
    part[0] = nameBytes.length;
    part.set(nameBytes, 1);
    part[1 + nameBytes.length] = 7;
    view.setUint16(2 + nameBytes.length, valueBytes.length, false);
    part.set(valueBytes, 4 + nameBytes.length);
    parts.push(part);
  }
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

export function encodeMessage(headers, payload) {
  const headerBytes = encodeHeaders(headers);
  const totalLength = 12 + headerBytes.length + payload.length + 4;
  const out = new Uint8Array(totalLength);
  const view = new DataView(out.buffer);

  view.setUint32(0, totalLength, false);
  view.setUint32(4, headerBytes.length, false);
  view.setUint32(8, crc32(out.subarray(0, 8)), false);
  out.set(headerBytes, 12);
  out.set(payload, 12 + headerBytes.length);
  view.setUint32(totalLength - 4, crc32(out.subarray(0, totalLength - 4)), false);
  return out;
}

export function encodeAudioEvent(pcmBytes) {
  return encodeMessage(
    {
      ":message-type": "event",
      ":event-type": "AudioEvent",
      ":content-type": "application/octet-stream",
    },
    pcmBytes,
  );
}

export function decodeMessage(buffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const totalLength = view.getUint32(0, false);
  const headersLength = view.getUint32(4, false);

  const headers = {};
  let offset = 12;
  const headersEnd = 12 + headersLength;
  while (offset < headersEnd) {
    const nameLength = bytes[offset];
    const name = textDecoder.decode(bytes.subarray(offset + 1, offset + 1 + nameLength));
    offset += 1 + nameLength + 1; // skip the value-type byte (always 7 from Transcribe)
    const valueLength = view.getUint16(offset, false);
    offset += 2;
    headers[name] = textDecoder.decode(bytes.subarray(offset, offset + valueLength));
    offset += valueLength;
  }

  return { headers, payload: bytes.subarray(headersEnd, totalLength - 4) };
}

// Float32 samples (-1..1) at inputRate → 16-bit little-endian PCM bytes at outputRate.
export function floatTo16BitPcm(samples, inputRate, outputRate) {
  let resampled = samples;
  if (inputRate !== outputRate) {
    const ratio = inputRate / outputRate;
    const length = Math.floor(samples.length / ratio);
    resampled = new Float32Array(length);
    for (let i = 0; i < length; i += 1) {
      const position = i * ratio;
      const index = Math.floor(position);
      const fraction = position - index;
      const next = samples[Math.min(index + 1, samples.length - 1)];
      resampled[i] = samples[index] * (1 - fraction) + next * fraction;
    }
  }

  const out = new Uint8Array(resampled.length * 2);
  const view = new DataView(out.buffer);
  for (let i = 0; i < resampled.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, resampled[i]));
    view.setInt16(i * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
  }
  return out;
}
